import { useEffect, useState } from "react";
import { HardDrive, Loader2, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { fmtBytes } from "./docUtils";

const QTY = [1, 2, 5, 10];

/** Admin buys extra storage packs (size & price set by the platform owner) through Stripe Checkout. */
export function StorageAddonDialog({ open, onClose, onApplied }) {
  const [offer, setOffer] = useState(null);
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setQty(1); api.get("/docs/storage/addon").then((r) => setOffer(r.data)).catch(() => setOffer(null)); } }, [open]);

  const buy = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/docs/storage/checkout", { quantity: qty, origin_url: window.location.origin });
      if (data.applied) { toast.success(`+${data.gb} GB added · your subscription is now +$${data.monthly_usd.toFixed(2)}/mo`); onApplied?.(data.quota); onClose(); return; }
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start checkout"); setBusy(false); }
  };

  const total = offer ? offer.price_usd * qty : 0;
  const gb = offer ? offer.pack_gb * qty : 0;
  const dueToday = offer ? Math.round(offer.prorate_per_pack_usd * qty * 100) / 100 : 0;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="storage-addon-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><HardDrive className="h-5 w-5 text-primary" /> Add storage</DialogTitle>
          <DialogDescription>{offer ? `Each pack adds ${offer.pack_gb} GB to this location for $${offer.price_usd.toFixed(2)}/month, added to your subscription renewals.` : "Loading…"}</DialogDescription>
        </DialogHeader>
        {offer && (
          <div className="space-y-4">
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm" data-testid="storage-addon-current">
              <div className="flex items-center justify-between"><span className="text-slate-600">Current limit</span><span className="font-semibold tabular-nums">{fmtBytes(offer.quota.limit_bytes)}</span></div>
              <div className="flex items-center justify-between"><span className="text-slate-600">Used</span><span className="tabular-nums">{fmtBytes(offer.quota.used_bytes)}</span></div>
            </div>
            <div>
              <div className="mb-1.5 text-xs uppercase tracking-widest text-slate-500">How many packs?</div>
              <div className="grid grid-cols-4 gap-2">
                {QTY.map((n) => (
                  <button key={n} type="button" onClick={() => setQty(n)} data-testid={`storage-qty-${n}`}
                    className={`rounded-xl border px-3 py-2.5 text-center transition ${qty === n ? "border-primary bg-primary/5 ring-2 ring-primary/20" : "border-slate-200 hover:bg-slate-50"}`}>
                    <div className="text-base font-semibold text-slate-900">+{offer.pack_gb * n} GB</div>
                    <div className="text-[11px] text-slate-500">${(offer.price_usd * n).toFixed(2)}</div>
                  </button>
                ))}
              </div>
            </div>
            <div className="rounded-xl bg-slate-900 px-4 py-3 text-white" data-testid="storage-addon-summary">
              <div className="flex items-center justify-between">
                <div><div className="text-[11px] uppercase tracking-widest text-slate-400">New limit</div><div className="font-semibold tabular-nums">{fmtBytes(offer.quota.limit_bytes + gb * 1024 ** 3)}</div></div>
                <div className="text-right"><div className="text-[11px] uppercase tracking-widest text-slate-400">Subscription</div><div className="text-xl font-bold tabular-nums">+${total.toFixed(2)}<span className="text-sm font-medium text-slate-300">/mo</span></div></div>
              </div>
              <div className="mt-2 border-t border-white/10 pt-2 text-xs text-slate-300" data-testid="storage-addon-due">
                {dueToday > 0
                  ? <>Due today: <b className="text-white">${dueToday.toFixed(2)}</b> — prorated for the {offer.prorate_days} day{offer.prorate_days === 1 ? "" : "s"} left in your current period. Renewals then include the full amount.</>
                  : <>Nothing due today — the add-on is included in your next subscription payment.</>}
              </div>
            </div>
            {!offer.can_buy && <div className="text-xs text-amber-700" data-testid="storage-addon-unavailable">Only admins can buy storage, or add-ons are currently not for sale.</div>}
          </div>
        )}
        <DialogFooter>
          <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="storage-addon-cancel">Cancel</button>
          <button type="button" onClick={buy} disabled={busy || !offer?.can_buy} className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-60" data-testid="storage-addon-buy">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />} {dueToday >= 0.5 ? `Pay $${dueToday.toFixed(2)} & add storage` : "Add storage"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
